import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceService } from '../../services/invoiceService';
import { Invoice, INVOICE_STATUS_LABELS, InvoiceStatus } from '../../types/invoice';
import { formatCurrency } from '../../utils/formatters';
import { generateInvoicePDF } from '../../utils/invoicePdfGenerator';

interface InvoiceDetailProps {
  invoiceId: string;
  onEdit: (invoice: Invoice) => void;
  onBack: () => void;
  onPayment?: (invoice: Invoice) => void;
}

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bgBase:         '#f4efe2',
  bgCard:         '#ffffff',
  bgElevated:     '#faf7ee',
  gold:           '#d97706',
  goldLight:      '#fbbf24',
  goldHover:      '#b45309',
  textPrimary:    '#1c2033',
  textSecondary:  '#5b5a52',
  textMuted:      '#8a8778',
  borderNormal:   'rgba(92,78,51,0.14)',
  borderSubtle:   'rgba(92,78,51,0.22)',
  fontHeading:    "'Inter', sans-serif",
  fontLabel:      "'Inter', sans-serif",
  fontBody:       "'Inter', sans-serif",
};

// ─── Status badge ───────────────────────────────────────────────────────────────
const statusBadgeStyle = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    DRAFT:           { bg: 'rgba(120,120,140,0.15)',  color: '#9a9ab0',  border: 'rgba(120,120,140,0.3)' },
    SENT:            { bg: 'rgba(90,140,201,0.12)',   color: '#5a8cc9',  border: 'rgba(90,140,201,0.25)' },
    PAID:            { bg: 'rgba(90,201,140,0.12)',   color: '#5ac98c',  border: 'rgba(90,201,140,0.25)' },
    PARTIALLY_PAID:  { bg: 'rgba(92,78,51,0.22)',   color: '#d97706',  border: 'rgba(92,78,51,0.35)' },
    OVERDUE:         { bg: 'rgba(201,90,90,0.12)',    color: '#c95a5a',  border: 'rgba(201,90,90,0.25)'  },
    CANCELLED:       { bg: 'rgba(80,80,80,0.15)',     color: '#555570',  border: 'rgba(80,80,80,0.25)'   },
  };
  const s = map[status] ?? map.DRAFT;
  return {
    display:         'inline-flex',
    alignItems:      'center',
    padding:         '3px 12px',
    borderRadius:    20,
    fontSize:        10,
    fontFamily:      C.fontLabel,
    letterSpacing:   '0.18em',
    fontWeight:      600,
    background:      s.bg,
    color:           s.color,
    border:          `1px solid ${s.border}`,
    textTransform:   'uppercase' as const,
  };
};

// ─── Shared table styles ────────────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  fontFamily:    C.fontLabel,
  fontSize:      9,
  letterSpacing: '0.22em',
  color:         C.textMuted,
  textTransform: 'uppercase',
  padding:       '10px 14px',
  background:    C.bgElevated,
  borderBottom:  `1px solid ${C.borderSubtle}`,
  fontWeight:    600,
  whiteSpace:    'nowrap',
};

const tdStyle: React.CSSProperties = {
  fontFamily:   C.fontBody,
  fontSize:     12,
  color:        C.textSecondary,
  padding:      '11px 14px',
  borderBottom: `1px solid ${C.borderNormal}`,
};

// ─── Section card wrapper ───────────────────────────────────────────────────────
const sectionCard: React.CSSProperties = {
  background:   C.bgCard,
  border:       `1px solid ${C.borderNormal}`,
  borderRadius: 6,
  overflow:     'hidden',
};

const sectionCardElevated: React.CSSProperties = {
  background:   C.bgElevated,
  border:       `1px solid ${C.borderSubtle}`,
  borderRadius: 6,
  overflow:     'hidden',
};

// ─── Section header strip ───────────────────────────────────────────────────────
const sectionHeaderStrip = (label: string) => (
  <div style={{
    display:        'flex',
    alignItems:     'center',
    gap:            8,
    padding:        '10px 20px',
    borderBottom:   `1px solid ${C.borderNormal}`,
    background:     'rgba(241,245,249,1)',
  }}>
    <div style={{ width: 3, height: 14, background: C.gold, borderRadius: 2 }} />
    <span style={{
      fontFamily:    C.fontLabel,
      fontSize:      9,
      letterSpacing: '0.2em',
      color:         C.textMuted,
      textTransform: 'uppercase',
    }}>
      {label}
    </span>
  </div>
);

export const InvoiceDetail: React.FC<InvoiceDetailProps> = ({ invoiceId, onEdit, onBack, onPayment }) => {
  const queryClient = useQueryClient();
  const [hoveredBtn, setHoveredBtn] = React.useState<string | null>(null);

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn:  () => invoiceService.getInvoice(invoiceId),
  });

  // Debug: Log invoice data when it loads
  React.useEffect(() => {
    if (invoice) {
      console.log('=== InvoiceDetail Component - Invoice Loaded ===');
      console.log('Invoice Number:', invoice.invoiceNumber);
      console.log('Items with serial numbers:');
      invoice.items?.forEach((item, idx) => {
        if (item.serialNumbers && item.serialNumbers.length > 0) {
          console.log(`  Item ${idx + 1}: ${item.description}`);
          console.log(`  Serial Numbers:`, item.serialNumbers);
        }
      });
    }
  }, [invoice]);

  const markAsSentMutation = useMutation({
    mutationFn: invoiceService.markInvoiceAsSent,
    onSuccess:  () => {
      console.log('Invoice marked as sent successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      alert('Invoice marked as sent successfully!');
    },
    onError: (error: any) => {
      console.error('Error marking invoice as sent:', error);
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to mark invoice as sent'}`);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: invoiceService.cancelInvoice,
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
    },
  });

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year:  'numeric',
      month: 'long',
      day:   'numeric',
    });

  const isOverdue = (dueDate: string, status: InvoiceStatus) =>
    new Date(dueDate) < new Date() && !['PAID', 'CANCELLED'].includes(status);

  const handleMarkAsSent = () => {
    console.log('Mark as Sent clicked, invoiceId:', invoiceId);
    if (window.confirm(`Mark invoice ${invoice?.invoiceNumber} as sent?`)) {
      markAsSentMutation.mutate(invoiceId);
    }
  };

  const handleCancel = () => {
    if (window.confirm(`Cancel invoice ${invoice?.invoiceNumber}?`)) {
      cancelMutation.mutate(invoiceId);
    }
  };

  const handlePrint      = () => window.print();
  const handleExportPDF  = () => { if (invoice) generateInvoicePDF(invoice); };

  // ── Action button helper ──────────────────────────────────────────────────────
  const actionBtn = (
    id:       string,
    label:    string,
    onClick:  () => void,
    variant:  'gold' | 'ghost' | 'danger' | 'green',
    disabled?: boolean,
  ) => {
    const variantStyles: Record<string, React.CSSProperties> = {
      gold:   { background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgCard,    border: 'none' },
      ghost:  { background: 'transparent',                                        color: C.textSecondary, border: `1px solid ${C.borderSubtle}` },
      danger: { background: 'transparent',                                        color: '#c95a5a',   border: '1px solid rgba(201,90,90,0.3)' },
      green:  { background: 'transparent',                                        color: '#5ac98c',   border: '1px solid rgba(90,201,140,0.3)' },
    };
    const hovered     = hoveredBtn === id;
    const hoverAdjust: React.CSSProperties = hovered
      ? variant === 'gold'
        ? { background: `linear-gradient(135deg, ${C.goldHover}, ${C.gold})`, transform: 'translateY(-1px)' }
        : { background: C.bgElevated }
      : {};
    return (
      <button
        key={id}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setHoveredBtn(id)}
        onMouseLeave={() => setHoveredBtn(null)}
        style={{
          fontFamily:    C.fontLabel,
          fontSize:      9,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          padding:       '8px 18px',
          borderRadius:  4,
          cursor:        disabled ? 'not-allowed' : 'pointer',
          opacity:       disabled ? 0.5 : 1,
          transition:    'all 0.2s ease',
          whiteSpace:    'nowrap',
          ...variantStyles[variant],
          ...hoverAdjust,
        }}
      >
        {label}
      </button>
    );
  };

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bgBase, padding: '32px 16px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ ...sectionCard, padding: 32 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} style={{
                height:       i === 1 ? 28 : 16,
                background:   C.bgElevated,
                borderRadius: 4,
                marginBottom: 16,
                width:        i === 1 ? '35%' : '100%',
                opacity:      0.6,
              }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────────
  if (error || !invoice) {
    return (
      <div style={{
        minHeight:       '100vh',
        background:      C.bgBase,
        padding:         '32px 16px',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
      }}>
        <div style={{ ...sectionCard, padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: C.fontBody, color: C.textMuted, marginBottom: 20 }}>
            Invoice not found or failed to load
          </p>
          <button
            onClick={onBack}
            style={{
              fontFamily:    C.fontLabel,
              fontSize:      10,
              letterSpacing: '0.18em',
              color:         C.bgCard,
              background:    `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              border:        'none',
              borderRadius:  4,
              padding:       '10px 24px',
              cursor:        'pointer',
              textTransform: 'uppercase',
            }}
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  const overdue = isOverdue(invoice.dueDate, invoice.status);

  return (
    <div style={{ minHeight: '100vh', background: C.bgBase, padding: '32px 16px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* ── Page Header ──────────────────────────────────────────────────────── */}
        <div style={{
          marginBottom:   32,
          display:        'flex',
          alignItems:     'flex-start',
          justifyContent: 'space-between',
          flexWrap:       'wrap',
          gap:            16,
        }}>
          {/* Left: back + title + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={onBack}
              onMouseEnter={() => setHoveredBtn('back')}
              onMouseLeave={() => setHoveredBtn(null)}
              style={{
                background:  'transparent',
                border:      'none',
                color:       hoveredBtn === 'back' ? C.gold : C.textMuted,
                cursor:      'pointer',
                padding:     4,
                transition:  'color 0.2s',
                lineHeight:  0,
              }}
            >
              <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div>
              <h1 style={{
                fontFamily:    C.fontHeading,
                fontSize:      36,
                fontWeight:    300,
                color:         C.textPrimary,
                letterSpacing: '0.04em',
                margin:        0,
                lineHeight:    1.1,
              }}>
                Invoice <span style={{ color: C.gold }}>{invoice.invoiceNumber}</span>
              </h1>
              <div style={{
                width:      48,
                height:     1,
                background: `linear-gradient(to right, ${C.gold}, transparent)`,
                marginTop:  6,
              }} />
            </div>

            <span style={statusBadgeStyle(invoice.status)}>
              {INVOICE_STATUS_LABELS[invoice.status]}
            </span>
          </div>

          {/* Right: action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {invoice.status === 'DRAFT' &&
              actionBtn(
                'mark-sent',
                markAsSentMutation.isPending ? 'Sending...' : 'Mark as Sent',
                handleMarkAsSent,
                'green',
                markAsSentMutation.isPending,
              )
            }
            {invoiceService.canEditInvoice(invoice.status) &&
              actionBtn('edit', 'Edit Invoice', () => onEdit(invoice), 'gold')
            }
            {onPayment && invoice.remainingAmount > 0 && invoice.status !== 'CANCELLED' &&
              actionBtn('payment', 'Add Payment', () => onPayment(invoice), 'green')
            }
            {actionBtn('pdf',   'Export PDF', handleExportPDF, 'ghost')}
            {actionBtn('print', 'Print',      handlePrint,     'ghost')}
            {invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' &&
              actionBtn(
                'cancel',
                cancelMutation.isPending ? 'Cancelling...' : 'Cancel',
                handleCancel,
                'danger',
                cancelMutation.isPending,
              )
            }
          </div>
        </div>

        {/* ── Invoice Card ──────────────────────────────────────────────────────── */}
        <div style={{
          background:   C.bgCard,
          border:       `1px solid ${C.borderSubtle}`,
          borderRadius: 8,
          overflow:     'hidden',
        }}>

          {/* Company / Document Header */}
          <div style={{
            borderBottom: `2px solid ${C.gold}`,
            padding:      32,
            background:   `linear-gradient(180deg, ${C.bgElevated} 0%, ${C.bgCard} 100%)`,
          }}>
            <div style={{
              display:        'flex',
              justifyContent: 'space-between',
              alignItems:     'flex-start',
              flexWrap:       'wrap',
              gap:            24,
            }}>
              {/* Company info */}
              <div>
                <h2 style={{
                  fontFamily:    C.fontHeading,
                  fontSize:      36,
                  fontWeight:    300,
                  color:         C.textPrimary,
                  margin:        '0 0 12px 0',
                  letterSpacing: '0.03em',
                }}>
                  {invoice.template?.templateCompanyName || invoice.company?.name || 'Your Company'}
                </h2>
                {invoice.company?.address && (
                  <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, lineHeight: 1.8 }}>
                    <p style={{ margin: 0 }}>{invoice.company.address}</p>
                    <p style={{ margin: 0 }}>
                      {invoice.company.city && `${invoice.company.city}, `}
                      {invoice.company.state} {invoice.company.postalCode}
                    </p>
                    {invoice.company.country && <p style={{ margin: 0 }}>{invoice.company.country}</p>}
                    {invoice.company.email  && <p style={{ margin: 0 }}>Email: {invoice.company.email}</p>}
                    {invoice.company.phone  && <p style={{ margin: 0 }}>Tel: {invoice.company.phone}</p>}
                  </div>
                )}
              </div>

              {/* Invoice metadata */}
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  background:    `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                  padding:       '10px 28px',
                  borderRadius:  4,
                  marginBottom:  16,
                  display:       'inline-block',
                }}>
                  <span style={{
                    fontFamily:    C.fontLabel,
                    fontSize:      18,
                    fontWeight:    700,
                    color:         C.bgCard,
                    letterSpacing: '0.15em',
                  }}>
                    INVOICE
                  </span>
                </div>

                <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, lineHeight: 2 }}>
                  {/* Invoice # */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.18em',
                      color:         C.textMuted,
                      textTransform: 'uppercase',
                    }}>Invoice #</span>
                    <span style={{ color: C.textPrimary }}>{invoice.invoiceNumber}</span>
                  </div>
                  {/* Issue Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.18em',
                      color:         C.textMuted,
                      textTransform: 'uppercase',
                    }}>Date</span>
                    <span style={{ color: C.textPrimary }}>{formatDate(invoice.issueDate)}</span>
                  </div>
                  {/* Due Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                    <span style={{
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.18em',
                      color:         C.textMuted,
                      textTransform: 'uppercase',
                    }}>Due Date</span>
                    <span style={{
                      color:      overdue ? '#c95a5a' : C.textPrimary,
                      fontWeight: overdue ? 600 : 400,
                    }}>
                      {formatDate(invoice.dueDate)}
                    </span>
                  </div>
                  {overdue && (
                    <div style={{
                      marginTop:     4,
                      padding:       '2px 8px',
                      background:    'rgba(201,90,90,0.12)',
                      border:        '1px solid rgba(201,90,90,0.25)',
                      borderRadius:  3,
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.2em',
                      color:         '#c95a5a',
                      textTransform: 'uppercase',
                    }}>
                      OVERDUE
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div style={{ padding: 32 }}>

            {/* Bill To + Payment Details */}
            <div style={{
              display:             'grid',
              gridTemplateColumns: '2fr 1fr',
              gap:                 24,
              marginBottom:        32,
            }}>
              {/* Bill To */}
              <div style={{ ...sectionCardElevated }}>
                {sectionHeaderStrip('Bill To')}
                <div style={{ padding: 24 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <p style={{ fontFamily: C.fontHeading, fontSize: 20, color: C.textPrimary, margin: 0, fontWeight: 400 }}>
                      {invoice.customer?.name}
                    </p>
                    {invoice.customer?.address && (
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                        {invoice.customer.address}
                      </p>
                    )}
                    {(invoice.customer?.city || invoice.customer?.state || invoice.customer?.postalCode) && (
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                        {invoice.customer.city && `${invoice.customer.city}, `}
                        {invoice.customer.state} {invoice.customer.postalCode}
                      </p>
                    )}
                    {invoice.customer?.country && (
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                        {invoice.customer.country}
                      </p>
                    )}
                    {invoice.customer?.email && (
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                        <span style={{
                          color:         C.textMuted,
                          fontFamily:    C.fontLabel,
                          fontSize:      8,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          marginRight:   6,
                        }}>Email</span>
                        {invoice.customer.email}
                      </p>
                    )}
                    {invoice.customer?.phone && (
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                        <span style={{
                          color:         C.textMuted,
                          fontFamily:    C.fontLabel,
                          fontSize:      8,
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          marginRight:   6,
                        }}>Phone</span>
                        {invoice.customer.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div style={{ ...sectionCardElevated }}>
                {sectionHeaderStrip('Payment Details')}
                <div style={{ padding: 24 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{
                        fontFamily:    C.fontLabel,
                        fontSize:      8,
                        letterSpacing: '0.15em',
                        color:         C.textMuted,
                        textTransform: 'uppercase',
                      }}>Total Amount</span>
                      <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{
                        fontFamily:    C.fontLabel,
                        fontSize:      8,
                        letterSpacing: '0.15em',
                        color:         C.textMuted,
                        textTransform: 'uppercase',
                      }}>Paid Amount</span>
                      <span style={{ fontFamily: C.fontBody, fontSize: 12, color: '#5ac98c' }}>
                        {formatCurrency(invoice.paidAmount)}
                      </span>
                    </div>
                    <div style={{
                      borderTop:   `1px solid ${C.borderSubtle}`,
                      paddingTop:  10,
                      display:     'flex',
                      justifyContent: 'space-between',
                      alignItems:  'baseline',
                    }}>
                      <span style={{
                        fontFamily:    C.fontLabel,
                        fontSize:      8,
                        letterSpacing: '0.15em',
                        color:         C.textPrimary,
                        textTransform: 'uppercase',
                      }}>Balance Due</span>
                      <span style={{
                        fontFamily: C.fontHeading,
                        fontSize:   20,
                        fontWeight: 300,
                        color:      invoice.remainingAmount > 0 ? '#c95a5a' : '#5ac98c',
                      }}>
                        {formatCurrency(invoice.remainingAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div style={{
              overflowX:    'auto',
              border:       `1px solid ${C.borderSubtle}`,
              borderRadius: 6,
              marginBottom: 28,
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Qty</th>
                    <th style={{ ...thStyle, textAlign: 'right',  width: 110 }}>Unit Price</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: 80 }}>Tax %</th>
                    <th style={{ ...thStyle, textAlign: 'right',  width: 120 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item) => (
                    <tr key={item.id}>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>
                        <div style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textPrimary }}>
                          {item.description}
                        </div>
                        {item.item?.unit && (
                          <div style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                            Unit: {item.item.unit}
                          </div>
                        )}
                        {item.serialNumbers && item.serialNumbers.length > 0 && (
                          <div style={{
                            marginTop:  6,
                            padding:    '6px 10px',
                            background: 'rgba(90,140,201,0.08)',
                            border:     '1px solid rgba(90,140,201,0.2)',
                            borderRadius: 4,
                          }}>
                            <span style={{
                              fontFamily:    C.fontLabel,
                              fontSize:      8,
                              letterSpacing: '0.15em',
                              color:         '#5a8cc9',
                              textTransform: 'uppercase',
                            }}>
                              Serial Numbers
                            </span>
                            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {item.serialNumbers.map((serial, idx) => (
                                <div key={idx} style={{ fontFamily: 'monospace', fontSize: 11, color: '#5a8cc9' }}>
                                  • {serial}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ ...tdStyle, textAlign: 'right'  }}>{formatCurrency(item.rate)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{item.taxRate ? `${item.taxRate}%` : '—'}</td>
                      <td style={{
                        ...tdStyle,
                        textAlign:  'right',
                        fontFamily: C.fontHeading,
                        fontSize:   15,
                        color:      C.goldLight,
                      }}>
                        {formatCurrency(item.amount + (item.taxAmount || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Invoice Summary / Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28 }}>
              <div style={{
                width:        320,
                background:   C.bgElevated,
                border:       `1px solid ${C.borderSubtle}`,
                borderRadius: 6,
                overflow:     'hidden',
              }}>
                {/* Summary header */}
                <div style={{
                  padding:       '10px 20px',
                  borderBottom:  `1px solid ${C.borderSubtle}`,
                  background:    'rgba(241,245,249,1)',
                }}>
                  <span style={{
                    fontFamily:    C.fontLabel,
                    fontSize:      9,
                    letterSpacing: '0.2em',
                    color:         C.textMuted,
                    textTransform: 'uppercase',
                  }}>
                    Invoice Summary
                  </span>
                </div>

                <div style={{ padding: '16px 20px' }}>
                  {/* Subtotal */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.15em',
                      color:         C.textMuted,
                      textTransform: 'uppercase',
                    }}>Subtotal</span>
                    <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                      {formatCurrency(invoice.subtotal)}
                    </span>
                  </div>

                  {/* Tax */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                    <span style={{
                      fontFamily:    C.fontLabel,
                      fontSize:      8,
                      letterSpacing: '0.15em',
                      color:         C.textMuted,
                      textTransform: 'uppercase',
                    }}>Tax Amount</span>
                    <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                      {formatCurrency(invoice.taxAmount)}
                    </span>
                  </div>

                  {/* Grand Total */}
                  <div style={{
                    borderTop:   `1px solid ${C.borderSubtle}`,
                    paddingTop:  14,
                    marginBottom: invoice.paidAmount > 0 ? 10 : 0,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{
                        fontFamily:    C.fontLabel,
                        fontSize:      9,
                        letterSpacing: '0.18em',
                        color:         C.textPrimary,
                        textTransform: 'uppercase',
                      }}>Total Amount</span>
                      <span style={{
                        fontFamily:    C.fontHeading,
                        fontSize:      24,
                        fontWeight:    300,
                        color:         C.goldLight,
                        letterSpacing: '0.02em',
                      }}>
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>
                  </div>

                  {/* Paid / Remaining */}
                  {invoice.paidAmount > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{
                          fontFamily:    C.fontLabel,
                          fontSize:      8,
                          letterSpacing: '0.15em',
                          color:         C.textMuted,
                          textTransform: 'uppercase',
                        }}>Paid Amount</span>
                        <span style={{ fontFamily: C.fontBody, fontSize: 12, color: '#5ac98c' }}>
                          {formatCurrency(invoice.paidAmount)}
                        </span>
                      </div>
                      <div style={{ borderTop: `1px solid ${C.borderSubtle}`, paddingTop: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span style={{
                            fontFamily:    C.fontLabel,
                            fontSize:      9,
                            letterSpacing: '0.18em',
                            color:         C.textPrimary,
                            textTransform: 'uppercase',
                          }}>Balance Due</span>
                          <span style={{
                            fontFamily: C.fontHeading,
                            fontSize:   22,
                            fontWeight: 300,
                            color:      invoice.remainingAmount > 0 ? '#c95a5a' : '#5ac98c',
                          }}>
                            {formatCurrency(invoice.remainingAmount)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            {invoice.notes && (
              <div style={{ ...sectionCardElevated, marginBottom: 24 }}>
                {sectionHeaderStrip('Notes & Terms')}
                <div style={{ padding: '16px 20px' }}>
                  <p style={{
                    fontFamily:  C.fontBody,
                    fontSize:    12,
                    color:       C.textSecondary,
                    lineHeight:  1.7,
                    margin:      0,
                    whiteSpace:  'pre-wrap',
                  }}>
                    {invoice.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Payment History */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div style={{ ...sectionCardElevated }}>
                {sectionHeaderStrip('Payment History')}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Date</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Method</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Reference</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td style={{ ...tdStyle, textAlign: 'left' }}>
                            {formatDate(payment.paymentDate)}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'left' }}>
                            {payment.paymentMethod}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'left', color: C.textMuted }}>
                            {payment.reference || '—'}
                          </td>
                          <td style={{
                            ...tdStyle,
                            textAlign:  'right',
                            fontFamily: C.fontHeading,
                            fontSize:   15,
                            color:      '#5ac98c',
                          }}>
                            {formatCurrency(payment.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
